import React, { useState } from "react";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Swal from "sweetalert2";

const MedicationSheetList = ({ records = [], onAdd, onEdit, onDelete }) => {
  const [filterText, setFilterText] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter logic
  const filteredRecords = records.filter((r) =>
    Object.values(r).some((v) =>
      String(v).toLowerCase().includes(filterText.toLowerCase())
    )
  );

  const totalRows = filteredRecords.length;
  const start = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalRows);

  // Delete confirmation
  const handleDelete = (id) => {
    Swal.fire({
      title: "Confirm Delete",
      text: "Do you want to delete this Medication Sheet?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(id);
        Swal.fire("Deleted!", "Medication Sheet has been removed.", "success");
      }
    });
  };

  // Export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Medication Sheets", 14, 10);
    const tableColumn = [
      "#", "Client Name", "Bed No", "MR No", "Consultant", "Ward",
      "Diagnosis", "Approved By", "Created By", "Updated By"
    ];
    const tableRows = filteredRecords.map((r, i) => [
      i + 1,
      r.name || r.client_name,
      r.bed_no,
      r.mr_no,
      r.consultant,
      r.ward,
      r.diagnosis,
      r.approved_by,
      r.creator?.name || "—",
      r.updater?.name || "—",
    ]);
    autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
    doc.save("medication_sheets.pdf");
  };

  // Custom Pagination
  const CustomPagination = ({
    onChangePage,
    onChangeRowsPerPage,
    currentPage,
    rowsPerPage,
    rowCount,
  }) => {
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    return (
      <div className="d-flex justify-content-between align-items-center p-3 border-top">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted" style={{ fontSize: "0.875rem" }}>
            Rows per page:
          </span>
          <select
            className="form-select form-select-sm"
            style={{ width: "80px" }}
            value={rowsPerPage}
            onChange={(e) =>
              onChangeRowsPerPage(Number(e.target.value), currentPage)
            }
          >
            {[5, 10, 15, 20].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(Math.max(1, currentPage - 1), rowCount)}
            disabled={currentPage === 1}
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>
          <span className="mx-3 text-muted" style={{ fontSize: "0.875rem" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="btn btn-sm border"
            onClick={() => onChangePage(Math.min(totalPages, currentPage + 1), rowCount)}
            disabled={currentPage === totalPages}
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>
    );
  };

  // Columns
  const columns = [
    {
      name: "S.No",
      cell: (row, index) => index + 1 + (currentPage - 1) * rowsPerPage,
      width: "70px",
    },
    { name: "Client", selector: (r) => r.name || r.client_name, sortable: true },
    { name: "Bed No", selector: (r) => r.bed_no, sortable: true },
    { name: "MR No", selector: (r) => r.mr_no, sortable: true },
    { name: "Consultant", selector: (r) => r.consultant, sortable: true },
    { name: "Ward", selector: (r) => r.ward, sortable: true },
    { name: "Diagnosis", selector: (r) => r.diagnosis },
    { name: "Approved By", selector: (r) => r.approved_by },
    { name: "Created By", selector: (r) => r.creator?.name || "—", sortable: true },
    { name: "Updated By", selector: (r) => r.updater?.name || "—", sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex justify-content-center gap-1">
          <button
            className="btn btn-sm btn-outline-primary"
            title="Edit"
            onClick={() => onEdit(row.id)}
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            title="Delete"
            onClick={() => handleDelete(row.id)}
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
      width: "130px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
        {/* Left: Export + Search */}
        <div className="d-flex align-items-center gap-2 position-relative">
          <div className="dropdown">
            <button
              className="btn btn-label-primary btn-sm dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ri-download-2-line me-1"></i> Export
            </button>
            <ul className="dropdown-menu shadow-sm">
              <li>
                <button className="dropdown-item" onClick={handleExportPDF}>
                  <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                </button>
              </li>
              <li>
                <CSVLink
                  data={filteredRecords}
                  filename="medication_sheets.csv"
                  className="dropdown-item"
                >
                  <i className="ri-file-excel-line me-1 text-success"></i> CSV
                </CSVLink>
              </li>
            </ul>
          </div>

          <input
            type="text"
            placeholder="Search Medication Sheets..."
            className="form-control form-control-sm"
            style={{ width: "260px" }}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Center Info */}
        <h6 className="mb-0 text-center text-muted flex-grow-1">
          Showing {start}-{end} of {totalRows} records
        </h6>

        {/* Right: Add button */}
        <div>
          <button className="btn btn-sm btn-primary" onClick={onAdd}>
            <i className="ri-add-line me-1"></i> Add New
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-2">
        <DataTable
          columns={columns}
          data={filteredRecords}
          pagination
          paginationPerPage={rowsPerPage}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={setRowsPerPage}
          onChangePage={setCurrentPage}
          paginationComponent={CustomPagination}
          highlightOnHover
          striped
          responsive
          noDataComponent={<div className="p-3 text-muted">No records found</div>}
        />
      </div>
    </div>
  );
};

export default MedicationSheetList;